import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import type { Data, FetchFn, MetricsReporter, RefSchemas } from '@wix/thunderbolt-becky-types'
import { presetIdsNode as presetIds } from './presetIdsNode'
import { getInvalidationHandler } from './invalidationHandler'
import { createSimpleMaterializer } from '@wix/materializer'
import { Container } from '@wix/thunderbolt-ioc'
import {
	CatharsisConstants,
	CatharsisSingleTransactionSymbol,
	CompNodeProviderSymbol,
	MaterializerFactorySymbol,
	OnInvalidationSymbol,
	css as catharsisIocLoader,
} from '@wix/thunderbolt-catharsis-ioc'
import { getBuilderPresets } from './widgetsPresetsCssMapper'
import { PresetsDataList, WidgetsToPresetToCss } from './types'
import { createFetchMemoization, fetchPresets } from './fetchPresets'

const RELEVANT_DATA_MAPS: Array<keyof Data> = ['document_data', 'presets']

export const findPresets = (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T = (fn) => fn()
): PresetsDataList => {
	const presets: PresetsDataList = []

	const container = Container()
	const catharsisConstants: CatharsisConstants = {
		componentsMappers: beckyModel.componentsMappers,
		experiments: beckyModel.experiments,
		isEditor: false,
		isExperimentOpen: (exp) => !!beckyModel.experiments[exp],
		schemas,
		isResponsiveMasterPage: beckyModel.isResponsive && beckyModel.pageCompId === 'masterPage',
	}

	container.load((bind) => catharsisIocLoader(bind, catharsisConstants))
	const onInvalidation = getInvalidationHandler(presets)
	container.bind(OnInvalidationSymbol).toConstantValue(onInvalidation)

	container.bind(MaterializerFactorySymbol).toConstantValue(createSimpleMaterializer)

	container.bind(CompNodeProviderSymbol).toConstantValue({
		getCompNodes: () => ({ presetIds }),
		getUsedDataMaps: () => RELEVANT_DATA_MAPS,
	})

	const singleTransaction = container.get(CatharsisSingleTransactionSymbol)

	singleTransaction.execute(beckyModel, runAndReport)

	return presets
}

export const fetchComponentPresets = (
	beckyModel: BeckyModel,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	schemas: RefSchemas,
	builderAppDefinitionIdsStr: string | undefined
): Promise<WidgetsToPresetToCss> => {
	const presets = metricsReporter.runAndReport(
		() => findPresets(beckyModel, schemas, metricsReporter.runAndReport),
		'tb-css-fetch-presets'
	)
	const builderAppDefinitionIds = builderAppDefinitionIdsStr ? JSON.parse(builderAppDefinitionIdsStr) : {}
	const builderPresetsToURLs = getBuilderPresets(presets, beckyModel.clientSpecMap, builderAppDefinitionIds)
	return fetchPresets(builderPresetsToURLs, createFetchMemoization(fetchFn), metricsReporter)
}
